import React, { useState, useCallback, useEffect, useRef } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader, StandaloneSearchBox } from "@react-google-maps/api";
import { getGlobal } from "@/getters/getGlobal";
import { useBranches } from "@/hooks/useClientLocation";
import cn from "classnames";
import styles from "./styles.module.scss";

const libraries = ["maps", "places"];

function InfoWindowMap({ branch }) {
  const [isOpen, setIsOpen] = useState(false);
  const lat = parseFloat(branch.latitude?.en ?? branch.latitude ?? "");
  const lng = parseFloat(branch.longitude?.en ?? branch.longitude ?? "");
  const validLatLng = !isNaN(lat) && !isNaN(lng);

  return validLatLng ? (
    <Marker position={{ lat, lng }} onClick={() => setIsOpen(true)}>
      {isOpen && (
        <InfoWindow onCloseClick={() => setIsOpen(false)}>
          <div>
            <h4 data-sourceflow-prop="branches.name">{branch.name?.en ?? branch.name}</h4>
            <div
              data-sourceflow-prop="branches.address"
              dangerouslySetInnerHTML={{ __html: branch.address?.en ?? branch.address }}
            />
            <p data-sourceflow-prop="branches.phone">{branch.phone?.en ?? branch.phone}</p>
            {branch?.show_about_this_branch_on_pin && (
              <a href={`/our-branches/${branch.url_slug}/`}>About this branch</a>
            )}
          </div>
        </InfoWindow>
      )}
    </Marker>
  ) : null;
}

export function ClientLocation({
  sectionId,
  branches = [],
  className = "",
  showAllBranches = false,
  title,
  titleColor,
  width = "w-full",
}) {
  const [map, setMap] = useState(null);
  const searchBoxRef = useRef(null);
  const [center, setCenter] = useState({ lat: 54.2138612, lng: -4.4604488 });
  const allBranches = useBranches();
  const global = getGlobal();
  const googleMapsApiKey = global["_theme.googleMapsApiKey"];

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey,
    libraries,
  });

  const onUnmount = useCallback(() => setMap(null), []);

  const displayBranches = showAllBranches
    ? allBranches
    : branches?.map((id) => allBranches.find((b) => String(b.id).trim() === String(id).trim())).filter(Boolean);

  const wrapperStyle = {
    width:
      width === "w-1-2"
        ? "50%"
        : width === "w-2-3"
        ? "66.6667%"
        : width === "w-80"
        ? "80%"
        : width === "container"
        ? "1280px"
        : "100%",
    margin: "0 auto",
  };

  const onPlacesChanged = () => {
    const place = searchBoxRef.current?.getPlaces?.()[0];
    if (place?.geometry?.location) {
      const location = place.geometry.location;
      const newCenter = { lat: location.lat(), lng: location.lng() };
      setCenter(newCenter);
      if (map) {
        map.panTo(newCenter);
        map.setZoom(14);
      }
    }
  };

  if (!googleMapsApiKey || !isLoaded) return null;

  return (
    <section id={sectionId} className={cn(styles.root, className)}>
      <div className="text-center mb-4">
        <h2 className={cn("h2", titleColor)} data-sourceflow-prop="title">
          {title}
        </h2>
      </div>
      <div className="sf-map-wrapper" style={wrapperStyle}>
        <StandaloneSearchBox onLoad={(ref) => (searchBoxRef.current = ref)} onPlacesChanged={onPlacesChanged}>
          <input type="text" placeholder="Search location..." className={styles.searchInput} />
        </StandaloneSearchBox>
        <div className={styles.map}>
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={center}
            zoom={5}
            onLoad={setMap}
            onUnmount={onUnmount}
          >
            {displayBranches?.map((branch) => (
              <InfoWindowMap key={branch.id} branch={branch} />
            ))}
          </GoogleMap>
        </div>
      </div>
    </section>
  );
}

export default ClientLocation;
